import * as fintech from "./fintechPlaybook";
import * as fashion from "./fashionPlaybook";
import * as nova from "./novaPlaybook";
import * as vs from "./vsPlaybook";
import * as tsc from "./tscPlaybook";
import { FINTECH_PERSONA_ID_PREFIX } from "./fintechCustomers";
import { FASHION_PERSONA_ID_PREFIX } from "./fashionCustomers";
import { NOVA_PERSONA_ID_PREFIX } from "./novaCustomers";
import { VS_PERSONA_ID_PREFIX } from "./vsCustomers";
import { TSC_CUSTOMERS, TSC_PERSONA_ID_PREFIX, TscCustomer, formatTscCustomerId } from "./tscCustomers";
import { MIKE, RILEY, TscPersona } from "../tsc/tscDemos";

/**
 * One lookup for everything the call console needs to run a demo against a
 * knows-nothing tester: the caller's script, the ground truth to check the agent
 * against, and the policy table that decides what the agent is allowed to promise.
 *
 * The console was originally hard-wired to Fintech (`agent.slug === "fintech"`), which
 * meant a second and third vertical could ship the data and still get the generic
 * retail chrome. This registry is the seam: a vertical appears here or it does not,
 * and every panel keys off that instead of a slug comparison.
 */

export interface PlaybookEntry {
  situation: string;
  opener: string;
  followUps: string[];
  facts: { label: string; value: string }[];
  expect: string[];
  watchOut: string[];
  /** Replaces the default "Call hints" rows (email / name / account) when set. */
  hints?: { k: string; v: string }[];
}

/** A policy row is per-vertical (icon/insider/member vs prime/plus/basic), so it is
 *  only ever read through the vertical's own `tierValue`. */
interface PolicyRow { label: string }

export interface VerticalPlaybook {
  /** The agent's name, for the copy on the policy card. */
  agentName: string;
  /** What this brand calls the band — "tier", "segment", "plan". Lower case, mid-sentence. */
  bandWord: string;
  policy: PolicyRow[];
  tierValue(row: PolicyRow, tier: string): string;
  personaPrefix: string;
  universalWatchOuts: string[];
  verificationNote?: string;
  /** "What this agent can do" — overrides the retail default in the call console. */
  capabilities?: { t: string; d: string }[];
  byCustomerId: Record<string, PlaybookEntry>;
}

// Tractor Supply: suggested lines per customer (tscPlaybook.ts), never scenario scripts.
// No tier table and no "real figures" card; the orders card carries the order facts.
// verifyWith: what the crew asks for first (see tsc/tscDemos.ts). The main TSC crew asks for the
// customer ID (the email only for a password reset); the multilingual clone asks for the email.
function tscEntry(c: TscCustomer, verifyWith: "customerId" | "email"): PlaybookEntry {
  const s = tsc.TSC_SUGGESTIONS[c.id];
  const lines = s?.tryLines ?? ["Hi, I'd like to cancel an order."];
  const latest = c.orders[0];
  const spelled = c.email.replace(/\./g, " dot ").replace("@", " at ");
  return {
    situation: "",
    opener: lines[0],
    followUps: lines.slice(1, 4),
    facts: [],
    expect: [],
    watchOut: [],
    hints: verifyWith === "customerId"
      ? [
          { k: "Customer ID", v: formatTscCustomerId(c.id) },
          { k: "Billing zip", v: c.zip.split("").join(" ") },
          { k: "Email (password reset)", v: spelled },
          ...(latest ? [{ k: "Latest order", v: latest.orderId }] : []),
        ]
      : [
          { k: "Say your email", v: spelled },
          { k: "Billing zip", v: c.zip.split("").join(" ") },
          ...(latest ? [{ k: "Latest order", v: latest.orderId }] : []),
        ],
  };
}

// The main TSC crew's verification, naming the agent (Riley, or Mike on the male-voice twin).
function tscVerificationNote(a: TscPersona): string {
  return `${a.name} verifies callers with the customer ID and the billing zip, and reads both back. For a password reset ${a.they} uses the email on the account and spells it back.`;
}

const REGISTRY: Record<string, VerticalPlaybook> = {
  tsc: {
    agentName: RILEY.name,
    bandWord: "tier",
    policy: [],
    tierValue: () => "",
    personaPrefix: TSC_PERSONA_ID_PREFIX,
    universalWatchOuts: tsc.UNIVERSAL_RULES_CUSTOMER_ID,
    verificationNote: tscVerificationNote(RILEY),
    capabilities: [
      { t: "Cancel online orders by SOP", d: "Inside 59 minutes on the call; store, warehouse and vendor requests after that" },
      { t: "Save the sale", d: "One cheaper offer after a cancel, with an emailed payment link" },
      { t: "Password resets and live transfers", d: "Reset email on the call, then a Neighbor's Club offer; anything else goes to a teammate with a briefing" },
    ],
    byCustomerId: Object.fromEntries(TSC_CUSTOMERS.map((c) => [c.id, tscEntry(c, "customerId")])),
  },
  fintech: {
    agentName: "Claire",
    bandWord: "plan",
    policy: fintech.TIER_POLICY,
    tierValue: fintech.tierValue as VerticalPlaybook["tierValue"],
    personaPrefix: FINTECH_PERSONA_ID_PREFIX,
    universalWatchOuts: fintech.UNIVERSAL_WATCHOUTS,
    capabilities: [
      { t: "Tier-aware promises", d: "Platinum, Gold and Silver each get a different answer" },
      { t: "Live Salesforce lookup", d: "Verifies the caller, then opens a case" },
      { t: "Branded email summary", d: "Sends a recap with the case reference" },
    ],
    byCustomerId: fintech.FINTECH_PLAYBOOKS,
  },
  "nuplay-fashion": {
    agentName: "Mira",
    bandWord: "tier",
    policy: fashion.TIER_POLICY,
    tierValue: fashion.tierValue as VerticalPlaybook["tierValue"],
    personaPrefix: FASHION_PERSONA_ID_PREFIX,
    universalWatchOuts: fashion.UNIVERSAL_WATCHOUTS,
    verificationNote: fashion.VERIFICATION_NOTE,
    byCustomerId: fashion.FASHION_PLAYBOOKS,
  },
  "nova-money": {
    agentName: "Meera",
    bandWord: "segment",
    policy: nova.TIER_POLICY,
    tierValue: nova.tierValue as VerticalPlaybook["tierValue"],
    personaPrefix: NOVA_PERSONA_ID_PREFIX,
    universalWatchOuts: nova.UNIVERSAL_WATCHOUTS,
    verificationNote: nova.VERIFICATION_NOTE,
    byCustomerId: nova.NOVA_PLAYBOOKS,
  },
  "victorias-secret": {
    agentName: "Ava",
    bandWord: "tier",
    policy: vs.TIER_POLICY,
    tierValue: vs.tierValue as VerticalPlaybook["tierValue"],
    personaPrefix: VS_PERSONA_ID_PREFIX,
    universalWatchOuts: vs.UNIVERSAL_WATCHOUTS,
    verificationNote: vs.VERIFICATION_NOTE,
    capabilities: [
      { t: "Verify, then help", d: "Identifies by phone or email, then verifies with the other factor before anything account-specific" },
      { t: "Recover a broken delivery", d: "Pickup, overnight or substitute — with a live warm transfer to the store to confirm the last unit" },
      { t: "Returns, gifting & escalation", d: "Assisted returns, a gifting concierge, and honest guardrails on cancels and refunds" },
    ],
    byCustomerId: vs.VS_PLAYBOOKS,
  },
};

// Mike: the main TSC crew cloned with a male voice. Same customers, hints and capabilities
// (Neighbor's Club line included); only the name and pronouns in the copy change.
REGISTRY["tsc-mike"] = {
  ...REGISTRY.tsc,
  agentName: MIKE.name,
  universalWatchOuts: tsc.universalRulesCustomerId(MIKE),
  verificationNote: tscVerificationNote(MIKE),
};

// The multilingual TSC demo uses the same customers and suggested lines; its crew still
// verifies with the email and billing zip.
REGISTRY["multilingual-support-agent"] = {
  ...REGISTRY.tsc,
  universalWatchOuts: tsc.UNIVERSAL_RULES,
  verificationNote: "Riley verifies every caller with the email on the account and the billing zip, and reads both back.",
  // Its crew has no Neighbor's Club offer after a password reset.
  capabilities: (REGISTRY.tsc.capabilities ?? []).map((c) =>
    c.t === "Password resets and live transfers"
      ? { ...c, d: "Reset email on the call; anything else goes to a teammate with a briefing" }
      : c),
  byCustomerId: Object.fromEntries(TSC_CUSTOMERS.map((c) => [c.id, tscEntry(c, "email")])),
};

export function verticalPlaybook(agentSlug?: string | null): VerticalPlaybook | undefined {
  return agentSlug ? REGISTRY[agentSlug] : undefined;
}

/** The caller's script for one persona, or undefined if this vertical has no playbook. */
export function playbookFor(agentSlug: string | undefined, personaId: string): PlaybookEntry | undefined {
  const v = verticalPlaybook(agentSlug);
  if (!v) return undefined;
  const raw = personaId.startsWith(v.personaPrefix) ? personaId.slice(v.personaPrefix.length) : personaId;
  return v.byCustomerId[raw];
}
